import React, { useContext } from 'react';
import { PriceDisplayTypeEnum, PriceDTO } from '@api/ringil-api.v2.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import COBadge, { BadgeType } from '@components/FigmaComponents/COBadge.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import SvgCoin from '@icons/coin-inverted.svg?react';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

type PriceTagProps = {
  count: number;
  pricePerUnitDto?: PriceDTO;
  unit: string;
  badgeType?: BadgeType;
  badgeVariant?: 'filled' | 'outlined';
};

const ManipulationUnitPriceTag: React.FC<PriceTagProps> = ({ count, pricePerUnitDto, unit, badgeType = 'positive', badgeVariant = 'filled' }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const id = React.useMemo(() => {
    const uid =
      typeof crypto !== 'undefined' && (crypto as any).randomUUID
        ? (crypto as any).randomUUID()
        : Math.random().toString(36).slice(2, 9);
    return `id-${uid}`;
  }, []);

  const priceDisplayType = loggedUser?.priceDisplayType || PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE;

  const formattedUnits = `${count.toLocaleString('cs', { maximumFractionDigits: 2}) || '-'} ${unit}`;

  const formattedPricePerUnit = (() => {
    if (!pricePerUnitDto) return '';

    switch (priceDisplayType) {
      case PriceDisplayTypeEnum.SHOW_PREFERRED_PRICE:
        return formatPrice(pricePerUnitDto.companyComputedPrice, pricePerUnitDto.companyComputedCurrency) + (unit ? `/${unit}` : '');
      case PriceDisplayTypeEnum.SHOW_ORIGINAL_PRICE:
        return formatPrice(pricePerUnitDto.price, pricePerUnitDto.currency) + (unit ? `/${unit}` : '');
      case PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE:
      default:
        return formatPrice(pricePerUnitDto.price, pricePerUnitDto.currency) + (unit ? `/${unit}` : '');
    }
  })();

  const tooltipEnabled = priceDisplayType !== PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE && pricePerUnitDto?.currency !== pricePerUnitDto?.companyComputedCurrency;

  const tooltipTitle = (() => {
    if (priceDisplayType === PriceDisplayTypeEnum.SHOW_PREFERRED_PRICE) return tr('PreferredPriceTag.originalPrice', 'Original Price');
    if (priceDisplayType === PriceDisplayTypeEnum.SHOW_ORIGINAL_PRICE) return tr('PreferredPriceTag.preferredPrice', 'Preferred Price');
    return '';
  })();

  const tooltipFormattedPricePerUnit = (() => {
    if (!pricePerUnitDto) return '';

    switch (priceDisplayType) {
      case PriceDisplayTypeEnum.SHOW_PREFERRED_PRICE:
        return formatPrice(pricePerUnitDto.price, pricePerUnitDto.currency) + (unit ? `/${unit}` : '');
      case PriceDisplayTypeEnum.SHOW_ORIGINAL_PRICE:
        return formatPrice(pricePerUnitDto.companyComputedPrice, pricePerUnitDto.companyComputedCurrency) + (unit ? `/${unit}` : '');
      case PriceDisplayTypeEnum.NO_SHOW_ADDITIONAL_PRICE:
      default:
        return '';
    }
  })();

  const tooltipConversionRate = (() => {
    if (!pricePerUnitDto) return '';

    const formattedRate = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 3 }).format(pricePerUnitDto.conversationRate);
    const label = tr('PreferredPriceTag.conversionRateLabel', 'Conversion rate');

    return `${label}: ${formattedRate} ${pricePerUnitDto.companyComputedCurrency} / ${pricePerUnitDto.currency}`;

  })();

  return (
    <div
      id={id}
    >
      {tooltipEnabled && (
        <CustomTooltip targetSelector={`#${id}`} content={
          <div
            className={`flex flex-col`}>
            <span className="pb-2">{tooltipTitle}</span>
            {
              tooltipFormattedPricePerUnit ? (
                <span className="inline-flex flex-wrap items-center gap-1">
                  <span className="whitespace-nowrap">{formattedUnits}</span>
                  <span className="mx-1 whitespace-nowrap">|</span>
                  <span className="font-bold whitespace-nowrap">{tooltipFormattedPricePerUnit}</span>
                </span>
              ) : (
                <span>{formattedUnits}</span>
              )
            }
            {pricePerUnitDto?.currency !== pricePerUnitDto?.companyComputedCurrency && (
              <span>{tooltipConversionRate}</span>
            )}
          </div>
        } />
      )}
      <COBadge variant={badgeVariant} type={badgeType}>
        {
          formattedPricePerUnit ? (
            <div className="flex flex-row items-center gap-2">
              <span className="inline-flex flex-wrap items-center gap-1">
                <span className="whitespace-nowrap">{formattedUnits}</span>
                <span className="mx-1 whitespace-nowrap">|</span>
                <span className="font-bold whitespace-nowrap">{formattedPricePerUnit}</span>
              </span>
              {tooltipEnabled && (
                <SvgCoin className="text-content-brand flex-shrink-0 self-center" />
              )}
            </div>
          ) : (
            <>
              <span>{formattedUnits}</span>
              {tooltipEnabled && (
                <SvgCoin className="text-content-brand flex-shrink-0 self-center" />
              )}
            </>
          )
        }
      </COBadge>
    </div>
  );
};

export default ManipulationUnitPriceTag;